import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, FlaskConical, LocateFixed, MapPin, Pill, Search } from 'lucide-react';
import { DEFAULT_SEARCH_RADIUS_KM, type DirectoryOrg } from '@his/shared';
import { publicGet, publicSession } from '@/lib/public-api';
import { qs } from '@/lib/api';
import { Badge, Button, Input, Select, TableSkeleton } from '@/components/ui/primitives';
import { EmptyState } from '@/components/ui/layout';

const TYPES = [
  { id: '', label: 'Everything' },
  { id: 'hospital', label: 'Hospitals', icon: Building2 },
  { id: 'lab', label: 'Labs', icon: FlaskConical },
  { id: 'pharmacy', label: 'Pharmacies', icon: Pill },
];

/**
 * The front door: pick a city, pick what you need.
 *
 * Results are one card per organisation, not per branch — a hospital with four
 * branches would otherwise fill the screen and tell the visitor nothing. The
 * branches are one tap away.
 */
export function FindPage() {
  const [city, setCity] = useState(publicSession.city() ?? '');
  const [type, setType] = useState('');
  const [search, setSearch] = useState('');
  const [nearby, setNearby] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  useEffect(() => {
    publicSession.setCity(city || null);
  }, [city]);

  const locateMe = () => {
    if (!('geolocation' in navigator)) {
      setLocateError('This browser cannot share your location');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNearby({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocating(false);
      },
      () => {
        setLocateError('Location was not shared — pick a city instead');
        setLocating(false);
      },
      { timeout: 10_000, maximumAge: 300_000 },
    );
  };

  const cities = useQuery({
    queryKey: ['public-cities'],
    queryFn: () => publicGet<{ city: string; facilities: number }[]>('/cities'),
  });

  // Nothing is chosen on a first visit, so start wherever most of the network
  // is rather than showing an empty screen.
  useEffect(() => {
    if (!city && cities.data && cities.data.length > 0) setCity(cities.data[0].city);
  }, [cities.data, city]);

  const results = useQuery({
    queryKey: ['public-facilities', city, type, search, nearby],
    queryFn: () =>
      publicGet<DirectoryOrg[]>(
        `/facilities${qs({
          city: nearby ? undefined : city || undefined,
          type: type || undefined,
          q: search || undefined,
          lat: nearby?.lat,
          lng: nearby?.lng,
          radiusKm: nearby ? DEFAULT_SEARCH_RADIUS_KM : undefined,
        })}`,
      ),
  });

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Find a hospital, lab or pharmacy</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Book an appointment or order medicines. The facility confirms every request.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5">
          <MapPin className="h-4 w-4 text-primary" />
          <Select
            value={nearby ? '' : city}
            onChange={(e) => {
              setNearby(null);
              setCity(e.target.value);
            }}
            className="w-48"
            disabled={nearby !== null}
          >
            <option value="">All cities</option>
            {cities.data?.map((row) => (
              <option key={row.city} value={row.city}>
                {row.city} ({row.facilities})
              </option>
            ))}
          </Select>
        </div>

        <Button
          type="button"
          size="sm"
          variant={nearby ? 'default' : 'outline'}
          className="h-10 sm:h-9"
          onClick={() => (nearby ? setNearby(null) : locateMe())}
          loading={locating}
        >
          <LocateFixed className="me-1.5 h-4 w-4" />
          {nearby ? `Within ${DEFAULT_SEARCH_RADIUS_KM} km of me` : 'Near me'}
        </Button>

        <div className="relative flex-1 min-w-[220px]">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or service, e.g. cardiology"
            className="ps-9"
          />
        </div>
      </div>

      {locateError && <p className="mb-3 text-sm text-destructive">{locateError}</p>}

      <div className="mb-5 flex flex-wrap gap-1.5">
        {TYPES.map((option) => (
          <Button
            key={option.id || 'all'}
            size="sm"
            className="h-10 sm:h-8"
            variant={type === option.id ? 'default' : 'outline'}
            onClick={() => setType(option.id)}
          >
            {option.icon ? <option.icon className="me-1.5 h-3.5 w-3.5" /> : null}
            {option.label}
          </Button>
        ))}
      </div>

      {results.isLoading ? (
        <TableSkeleton />
      ) : results.data && results.data.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {results.data.map((org) => (
            <Link
              key={org.tenantId}
              to={`/find/${org.slug}`}
              className="rounded-lg border bg-card p-4 transition-colors hover:border-primary focus-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{org.name}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {org.cities.join(', ')}
                    {org.branchCount > 1 ? ` · ${org.branchCount} branches` : ''}
                    {org.distanceKm !== null ? ` · ${org.distanceKm} km away` : ''}
                  </p>
                </div>
                {org.logoUrl && (
                  <img src={org.logoUrl} alt="" className="h-9 w-9 rounded object-contain" />
                )}
              </div>

              {org.about && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{org.about}</p>}

              <div className="mt-3 flex flex-wrap gap-1">
                {org.types.map((facilityType) => (
                  <Badge key={facilityType} variant="secondary">
                    {facilityType}
                  </Badge>
                ))}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Search}
          title="Nothing listed here yet"
          description={
            nearby
              ? `No listed facility is within ${DEFAULT_SEARCH_RADIUS_KM} km of you. Pick a city instead.`
              : city
                ? `No hospital, lab or pharmacy has published itself in ${city} yet. Try another city.`
                : 'No facility has published itself yet.'
          }
        />
      )}
    </>
  );
}
